import { AriaDialogProps, useDialog } from "@react-aria/dialog";
import { usePress } from "@react-aria/interactions";
import {
  AriaPopoverProps,
  Overlay,
  PlacementAxis,
  useOverlayTrigger,
  usePopover,
} from "@react-aria/overlays";
import { mergeProps } from "@react-aria/utils";
import {
  OverlayTriggerProps,
  OverlayTriggerState,
  useOverlayTriggerState,
} from "@react-stately/overlays";
import React, {
  ElementType,
  FC,
  HTMLAttributes,
  ReactNode,
  Ref,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import { tombac } from "../../shared";
import {
  FadeInAnimation,
  FadeOutAnimation,
} from "../../shared/animations/FadeAnimation";
import {
  PropsWithPropStyling,
  extractStylingProps,
  propStyling,
} from "../../shared/propStyling";
import { PopoverArrow } from "./PopoverArrow";

export type PopoverVariant = "default" | "inverted" | "danger";

export type PopoverBaseRenderProps = {
  isExiting: boolean;
  placement: PlacementAxis;
  state: OverlayTriggerState;
};

export type PopoverRenderProps = PopoverBaseRenderProps & {
  titleProps: HTMLAttributes<HTMLElement>;
};

export type PopoverTriggerRenderProps = PopoverBaseRenderProps & {
  triggerProps: HTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement> };
};

export type PopoverProps = PropsWithPropStyling<
  OverlayTriggerProps &
    AriaDialogProps &
    Pick<
      AriaPopoverProps,
      | "crossOffset"
      | "isKeyboardDismissDisabled"
      | "isNonModal"
      | "maxHeight"
      | "offset"
      | "placement"
      | "scrollRef"
      | "shouldCloseOnInteractOutside"
      | "shouldFlip"
      | "shouldUpdatePosition"
    > & {
      as?: ElementType;
      className?: string;
      children?: ReactNode | ((props: PopoverRenderProps) => ReactNode);

      /** The size of the popover. 'm' is default, 's' is smaller. */
      size?: "s" | "m";

      /**
       * The trigger element for the popover. This should be a function that
       * returns a ReactNode.
       *
       * @param props.triggerProps - The props to spread on the trigger element.
       */
      trigger(props: PopoverTriggerRenderProps): ReactNode;

      /**
       * The variant of the popover. 'default' is white, 'inverted' black and
       * 'danger' red.
       */
      variant?: PopoverVariant;
    }
>;

export const Popover: FC<PopoverProps> = ({
  as = "div",
  children,
  className,
  offset = 14,
  size = "m",
  trigger,
  variant = "default",
  ...otherProps
}) => {
  const [stylingProps, hookProps] = extractStylingProps(otherProps);

  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);

  const state = useOverlayTriggerState(hookProps);
  const [isExiting, setIsExiting] = useState(false);

  useLayoutEffect(() => {
    setIsExiting(!state.isOpen);
  }, [state.isOpen]);

  const { overlayProps, triggerProps: overlayTriggerProps } = useOverlayTrigger(
    { type: "dialog" },
    state,
    triggerRef,
  );
  const { pressProps: triggerProps } = usePress(overlayTriggerProps);

  const { arrowProps, placement, popoverProps } = usePopover(
    {
      ...hookProps,
      arrowBoundaryOffset: 8,
      arrowSize: 14,
      offset,
      popoverRef,
      triggerRef,
    },
    state,
  );
  const { dialogProps, titleProps } = useDialog(hookProps, popoverRef);

  const handleAnimationEnd = () => {
    setIsExiting(false);
  };

  const baseRenderProps = { isExiting, placement, state };

  return (
    <>
      {trigger({
        ...baseRenderProps,
        triggerProps: { ref: triggerRef, ...triggerProps },
      })}
      {(state.isOpen || isExiting) && (
        <Overlay isExiting={isExiting}>
          <Container
            {...stylingProps}
            {...mergeProps(overlayProps, popoverProps, dialogProps)}
            as={as}
            className={className}
            isExiting={isExiting}
            placement={placement}
            onAnimationEnd={handleAnimationEnd}
            ref={popoverRef}
            size={size}
            variant={variant}
          >
            <PopoverArrow
              {...arrowProps}
              placement={placement}
              variant={variant}
              viewBox="0 0 14 14"
            />
            {typeof children === "function"
              ? children({ ...baseRenderProps, titleProps })
              : children}
          </Container>
        </Overlay>
      )}
    </>
  );
};

type ContainerProps = Pick<PopoverProps, "size" | "variant"> & {
  isExiting: boolean;
  placement: PlacementAxis;
};

const containerSizes = tombac.variant<ContainerProps>("size", {
  m: css`
    padding: ${tombac.space(2)};
  `,
  s: css`
    padding: ${tombac.space(1)};
  `,
});

const containerVariants = tombac.variant<ContainerProps>("variant", {
  default: css`
    background-color: ${tombac.color.white};
    color: ${tombac.color.black};
  `,
  danger: css`
    background-color: ${tombac.color("danger", 400)};
    color: ${tombac.color.white};
  `,
  inverted: css`
    background-color: ${tombac.color.black};
    color: ${tombac.color.white};
  `,
});

const Container = styled.div<ContainerProps>`
  all: initial;
  animation: ${({ isExiting }) =>
      isExiting ? FadeOutAnimation : FadeInAnimation}
    0.1s ease-out forwards;

  border-radius: ${tombac.unit(3)};
  box-shadow: ${tombac.unit(0, 4, 16, 1)} rgba(0, 0, 0, 16%);
  box-sizing: border-box;
  display: block;

  ${tombac.typography.text}

  ${containerSizes}
  ${containerVariants}

  ${propStyling}
`;
